/**
 * ============================================================================
 * Azure AD SSO — the pure halves of the flow, as the backend actually performs it.
 * ============================================================================
 *
 * ⚠️ `/auth/sso/login` CANNOT BE CALLED WITH `fetch`. Its own description is *"Initiate Azure AD SSO
 * login. Redirects user to Microsoft login page"*, and probing confirms it: the endpoint answers a 3xx,
 * which `fetch` reports as `type: 'opaqueredirect'` with the `Location` hidden by CORS, and following it
 * throws `TypeError: Failed to fetch` because the target is Microsoft's origin. It must be a FULL-PAGE
 * NAVIGATION so the browser walks the redirect chain itself.
 *
 * THE ROUND TRIP, confirmed by completing a real sign-in:
 *
 *   1. this app  → GET {API}/auth/sso/login?next_url={where to end up}
 *   2. backend   → 302 to Microsoft
 *   3. Microsoft → the user authenticates
 *   4. Microsoft → GET {API}/auth/sso/callback?code=…&state=…
 *   5. backend   → 302 to  {ITS OWN CONFIGURED FRONTEND}/login?token={jwt}&next={the next_url}
 *   6. /login    → consumes the token, then goes to `next`
 *
 * ⚠️ STEP 5 LANDS ON `/login`, NOT ON THE `next_url` WE ASKED FOR. The backend chooses the landing path
 * itself and relays our value onward as `next` — note the name change, `next_url` going out and `next`
 * coming back. An observed landing URL:
 *
 *     http://localhost:5173/login?token=eyJhbGciOi…&next=http%3A//192.168.51.36%3A5173/auth/sso/callback
 *
 * That is why the token is consumed by `LoginPage` rather than by a dedicated callback route: a route the
 * backend never redirects to would be dead code, and worse, `next` pointing at one would send the browser
 * there AFTER the token had already been consumed — arriving with nothing and reporting a failure.
 *
 * ⚠️ AND THE FRONTEND ORIGIN IN STEP 5 IS THE BACKEND'S OWN CONFIGURATION, NOT OURS. The observed
 * redirect went to `http://localhost:5173`, which is this project's default dev origin
 * (`vite.config.ts` pins `port: 5173, strictPort: true`), so the round trip closes for `npm run dev`.
 * It does NOT close for anything served elsewhere — the `--port 5174` override in
 * `.claude/launch.json`, a LAN address, or a deployed host — because the backend will still send the
 * browser to whatever origin IT was configured with. Nothing in this file can fix that; the backend's
 * frontend-URL setting has to match where the app is actually served.
 */

/**
 * The URL to navigate to in order to start SSO.
 *
 * ⚠️ `nextUrl` MUST BE BUILT FROM OUR OWN ORIGIN by the caller, never from anything user-supplied. It is
 * a redirect target handed to a server that will send a browser there carrying a fresh session — the
 * textbook open-redirect surface, and `LoginPage` already carries a note about the `returnUrl` variant of
 * exactly this mistake.
 */
export function ssoLoginUrl(apiBase: string, nextUrl: string): string {
  return `${apiBase.replace(/\/+$/, '')}/auth/sso/login?next_url=${encodeURIComponent(nextUrl)}`;
}

/**
 * `value` when it is a path on our own origin, else `null`.
 *
 * A single leading slash only: `//evil.com` is protocol-relative, and browsers read `/\evil.com` the
 * same way, so both are refused. Password login's `returnUrl` and both ends of SSO go through here.
 */
export function sameOriginPath(value: string | null | undefined): string | null {
  const path = value?.trim();
  return path && /^\/(?![/\\])/.test(path) ? path : null;
}

/**
 * What to ask the backend to send us to once the token has been issued: `returnPath` — the page the
 * user was sent to sign in from, such as an emailed export link — or the dashboard.
 *
 * It is the DESTINATION AFTER sign-in, not a page that handles the sign-in, because the backend picks
 * the landing path itself (`/login`) and merely relays this value onward as `next`.
 *
 * ⚠️ IT DEGRADES ON PURPOSE. The backend redirects to ITS OWN configured frontend origin, which need not
 * be ours; when it is not, this absolute URL fails the same-origin check in `safeNextPath` and the user
 * lands on `/dashboard` instead. That is the right outcome — following a cross-origin value would be the
 * open redirect.
 */
export function ssoNextUrl(origin: string, returnPath?: string | null): string {
  return `${origin.replace(/\/+$/, '')}${sameOriginPath(returnPath) ?? '/dashboard'}`;
}

export type SsoLanding =
  | { kind: 'token'; token: string; returnTo: string }
  | { kind: 'error'; message: string }
  /** Neither — an ordinary visit to /login. `saw` lists the parameters, for the dev diagnostic. */
  | { kind: 'none'; saw: string[] };

/** The names the redirect could carry the app's own token under. `token` is the observed one. */
const TOKEN_KEYS = ['token', 'access_token', 'jwt'] as const;
/** Azure AD's own failure shape, relayed by the backend. */
const ERROR_KEYS = ['error_description', 'error', 'sso_error'] as const;

/**
 * Where to go once the token is consumed.
 *
 * ⚠️ `next` ARRIVES AS AN ABSOLUTE URL — `http://192.168.51.36:5173/auth/sso/callback` in the observed
 * redirect — so a plain "must start with /" check would reject every real value and silently send
 * everyone to the dashboard. It is parsed instead, and accepted ONLY when its origin matches ours;
 * anything else is an open redirect, which is precisely what an attacker would want from a parameter
 * that a freshly-authenticated browser follows.
 *
 * A relative path is also accepted, and the protocol-relative `//evil.com` — which does start with `/` —
 * is rejected with it.
 */
export function safeNextPath(next: string | null, origin: string): string {
  const fallback = '/dashboard';
  if (!next) return fallback;

  const value = next.trim();
  if (!value) return fallback;

  if (value.startsWith('/')) return sameOriginPath(value) ?? fallback;

  try {
    const url = new URL(value);
    if (url.origin !== origin) return fallback;
    // `http://host//evil.com` parses to the pathname `//evil.com`, so the path is checked too.
    return sameOriginPath(`${url.pathname}${url.search}${url.hash}`) ?? fallback;
  } catch {
    // Not a URL at all — treat as untrusted rather than guessing at a path.
    return fallback;
  }
}

/**
 * Reads the SSO outcome out of the landing URL.
 *
 * BOTH the query and the fragment are examined. The observed flow uses the query — a server-side
 * redirect cannot write a fragment — but reading both costs nothing and removes a class of silent
 * failure if the backend ever moves it.
 *
 * ⚠️ AN ERROR IS CHECKED BEFORE A TOKEN. Azure AD can come back with `error=access_denied` and no token;
 * reporting that as "no SSO parameters" would hide a reason worth showing the user.
 */
export function readSsoLanding(search: string, hash: string, origin: string): SsoLanding {
  const query = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const fragment = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);

  const read = (keys: readonly string[]): string | null => {
    for (const key of keys) {
      const value = query.get(key) ?? fragment.get(key);
      if (value?.trim()) return value.trim();
    }
    return null;
  };

  const error = read(ERROR_KEYS);
  if (error) return { kind: 'error', message: error };

  const token = read(TOKEN_KEYS);
  if (token) {
    return {
      kind: 'token',
      token,
      returnTo: safeNextPath(query.get('next') ?? query.get('next_url'), origin),
    };
  }

  return { kind: 'none', saw: [...new Set([...query.keys(), ...fragment.keys()])] };
}
